/**
 * App route catalog — single source for path order, permissions, and nav helpers.
 * Used by sidebar, first-allowed redirect, prefetch, and active matching.
 */

import { isRestaurantBusinessType } from '../utils/restaurantMode';

/**
 * Keep-alive shell surfaces (lazy chunks, warmed on login/intent).
 * Not bundled into the ConditionalRoutes shell module.
 */
export const SHELL_KEEPALIVE_PATHS = ['/', '/inventory', '/stock'];

/**
 * Default idle prefetch for managers (also the scheduleIdleNavPrefetch default —
 * adsVisualSmoke locks this exact list string in navRoutePrefetch.js).
 */
export const MANAGER_IDLE_PREFETCH_PATHS = [
  '/reports',
  '/sales',
  '/purchases',
  '/customers',
  '/expenses',
  '/settings',
  '/mitarbeiter',
  '/suppliers',
  '/orders',
];

/**
 * Worker idle prefetch — lazy registries + shell tables workers often open.
 * `/` POS chunk is warmed from Login via peekRoleType.
 */
export const WORKER_IDLE_PREFETCH_PATHS = [
  '/sales',
  '/restaurant',
  '/cuisine',
  '/inventory',
  '/stock',
  '/payments',
  '/expenses',
];

/**
 * Sidebar / first-allowed path order (path + permission only).
 * Presentation (labels/icons) lives in navigationForBusinessType.
 */
export function getAppNavPathEntries(businessType) {
  const isRestaurant = isRestaurantBusinessType(businessType);
  return [
    { path: '/', permission: 'dashboard' },
    // Restaurant: Kitchen monitor + stock + Ventes (sales permission).
    ...(isRestaurant
      ? [
          { path: '/cuisine', permission: 'sales' },
          { path: '/restaurant', permission: 'sales' },
          { path: '/sales', permission: 'sales' },
        ]
      : [{ path: '/sales', permission: 'sales' }]),
    { path: '/orders', permission: 'orders' },
    { path: '/customers', permission: 'customers' },
    // Stock group: supplier → product catalog → purchases → stock levels
    { path: '/suppliers', permission: 'suppliers' },
    { path: '/inventory', permission: 'inventory' },
    { path: '/purchases', permission: 'purchases' },
    { path: '/stock', permission: 'stock' },
    { path: '/inventur', permission: 'stock' },
    { path: '/expenses', permission: 'expenses' },
    { path: '/payments', permission: 'payments' },
    { path: '/payment-report', permission: 'reports' },
    { path: '/reports', permission: 'reports' },
    { path: '/mitarbeiter', permission: 'settings' },
    { path: '/settings', permission: 'settings' },
  ];
}

/**
 * First sidebar-allowed path for the current role (nav order).
 * Hot path: early-exit loop, no nav-item / icon construction.
 */
export function getFirstAllowedAppPath({ businessType, hasPermission }) {
  if (typeof hasPermission !== 'function') return '/';
  if (hasPermission('dashboard')) return '/';
  const entries = getAppNavPathEntries(businessType);
  for (let i = 1; i < entries.length; i += 1) {
    if (hasPermission(entries[i].permission)) return entries[i].path;
  }
  return '/';
}

/**
 * Exact / nested active match — avoids `/payments` lighting up on `/payment-report`.
 */
export function isNavPathActive(pathname, path) {
  const current = pathname || '/';
  if (!path || path === '/') return current === '/';
  return current === path || current.startsWith(`${path}/`);
}

export function isShellKeepAlivePath(path) {
  if (!path) return false;
  const bare = path.split('?')[0];
  return SHELL_KEEPALIVE_PATHS.includes(bare);
}
